import React from 'react';
import { Bug } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useDebugMode } from '../../../contexts/DebugModeContext';

/**
 * Admin-only switch that turns on the per-message debug panels in chat.
 * Renders nothing for non-admins, so the control is invisible to regular users.
 * Designed to sit inline in the ChatHeader controls row, next to the owner chip.
 */
const DebugModeToggle: React.FC = () => {
  const { currentUser } = useAuth();
  const { debugMode, toggleDebugMode } = useDebugMode();

  const isAdmin = currentUser?.role === 'admin';
  if (!isAdmin) return null;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={debugMode}
      onClick={toggleDebugMode}
      title="Admin only — show model reasoning (probability scores, parts state, raw output) on each answer"
      className={`flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full border transition-colors flex-shrink-0 cursor-pointer ${
        debugMode
          ? 'bg-[#6866ff] border-[#6866ff] text-white'
          : 'bg-[#f6f4f0] border-[#efebe2] text-[#999999] hover:bg-[#efebe2] hover:text-[#201e1a]'
      }`}
    >
      <Bug className="w-3.5 h-3.5" />
      <span className="text-xs font-medium">Debug</span>
      {/* Switch track */}
      <span
        className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
          debugMode ? 'bg-white/30' : 'bg-[#dddcd4]'
        }`}
      >
        <span
          className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
            debugMode ? 'translate-x-3.5' : 'translate-x-0.5'
          }`}
        />
      </span>
    </button>
  );
};

export default DebugModeToggle;
